import { newCampaignsApi } from '@/store/newCampaignService';
import {
  STEP_SETUP,
  STEP_RECIPIENTS_TYPE,
  STEP_RECIPIENTS_AUTO,
  STEP_RECIPIENTS_MANUAL,
  STEP_TEMPLATE_LIST,
  STEP_TEMPLATE_EDITOR,
  STEP_REVIEW,
} from '@/pages/newEmailCampaigns/createCampaign/constants/steps';
import { RECIPIENTS_TYPE_REGULAR, RECIPIENTS_TYPE_AUTOMATED, RECIPIENTS_TYPE_MODERATED } from '@/pages/newEmailCampaigns/constants/recipients';
import { campaignDoesNotBelongToCompany } from '@/pages/newEmailCampaigns/util/helpers';

// channel/type used by every audience + segment call in this flow
const CHANNEL = 'email';
// channel_type expected by the file upload endpoint
const FILE_CHANNEL_TYPE = 'email';

const getCollectionItems = (responseData = {}) => {
  const nestedData = responseData.data || {};

  return (Array.isArray(responseData) ? responseData : null)
    || responseData.items
    || responseData.attachments
    || responseData.segments
    || nestedData.items
    || nestedData.attachments
    || nestedData.segments
    || (Array.isArray(responseData.data) ? responseData.data : [])
    || responseData.results
    || [];
};

const initializeForm = () => ({
  step: STEP_SETUP,
  isSaving: false,
  isLoading: false,
  data: null,
  templateId: null,
  recipientsType: null,
  // segments attached to the campaign (system segments, e.g. shareholders/contacts lists)
  attachedSegments: [],
  // documents attached through /campaigns/:id/attachments
  attachedDocuments: [],
  // draft audience items (manual / file) added via the /audience endpoints
  audienceItems: [],
  scheduleData: null,
});

const initializeSingleTemplate = () => ({
  isLoading: false,
  data: null,
});

const initializeTemplates = () => ({
  isLoading: false,
  data: [],
});

const initializeCompanySettings = () => ({
  isLoading: false,
  data: null,
});

// system segments shown in the "Lists" tab of the recipients step
const initializeSystemSegments = () => ({
  isLoading: false,
  data: [],
});

const InitialState = {
  form: initializeForm(),
  singleTemplate: initializeSingleTemplate(),
  templates: initializeTemplates(),
  companySettings: initializeCompanySettings(),
  systemSegments: initializeSystemSegments(),
};

const campaignUsesAutomatedRecipients = (form) => (
  form.recipientsType === RECIPIENTS_TYPE_AUTOMATED
  || (form.data && form.data.is_recurrent)
);

const getCampaignSetupFields = (campaignData, scheduleData) => ({
  subject: campaignData && campaignData.subject,
  from_name: campaignData && campaignData.from_name,
  scheduled_at: (scheduleData && scheduleData.scheduled_at)
    || (campaignData && campaignData.scheduled_at),
  recurrence_time: (scheduleData && scheduleData.recurrence_time)
    || (campaignData && campaignData.recurrence_time),
  recurrence_timezone: (scheduleData && scheduleData.recurrence_timezone)
    || (campaignData && campaignData.recurrence_timezone),
});

const getChangedTruthyFields = (preservedFields, currentData) => (
  Object.keys(preservedFields).reduce((acc, field) => {
    const previousValue = preservedFields[field];
    const currentValue = currentData && currentData[field];

    if (previousValue && currentValue !== previousValue) {
      acc[field] = previousValue;
    }

    return acc;
  }, {})
);

const mutations = {
  // form mutations
  resetForm: (state) => Object.assign(state, {
    form: initializeForm(),
    singleTemplate: initializeSingleTemplate(),
    templates: initializeTemplates(),
    systemSegments: initializeSystemSegments(),
  }),
  nextStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_RECIPIENTS_TYPE;
        break;
      case STEP_RECIPIENTS_TYPE:
        nextStep = campaignUsesAutomatedRecipients(state.form)
          ? STEP_RECIPIENTS_AUTO
          : STEP_RECIPIENTS_MANUAL;
        break;
      case STEP_RECIPIENTS_AUTO:
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_TEMPLATE_LIST;
        break;
      case STEP_TEMPLATE_LIST:
        nextStep = STEP_TEMPLATE_EDITOR;
        break;
      case STEP_TEMPLATE_EDITOR:
        nextStep = STEP_REVIEW;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  backStep: (state) => {
    let nextStep;
    switch (state.form.step) {
      case STEP_SETUP:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_TYPE:
        nextStep = STEP_SETUP;
        break;
      case STEP_RECIPIENTS_AUTO:
      case STEP_RECIPIENTS_MANUAL:
        nextStep = STEP_RECIPIENTS_TYPE;
        break;
      case STEP_TEMPLATE_LIST:
        nextStep = campaignUsesAutomatedRecipients(state.form)
          ? STEP_RECIPIENTS_AUTO
          : STEP_RECIPIENTS_MANUAL;
        break;
      case STEP_TEMPLATE_EDITOR:
        nextStep = STEP_TEMPLATE_LIST;
        break;
      case STEP_REVIEW:
        nextStep = STEP_TEMPLATE_EDITOR;
        break;
      default:
        nextStep = STEP_SETUP;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        step: nextStep,
      }),
    });
  },
  gotoStep: (state, step) => Object.assign(state, {
    form: Object.assign(state.form, {
      step,
    }),
  }),
  setLoading: (state, isLoading) => Object.assign(state, {
    form: Object.assign(state.form, {
      isLoading,
    }),
  }),
  setSaving: (state, isSaving) => Object.assign(state, {
    form: Object.assign(state.form, {
      isSaving,
    }),
  }),
  setCampaignData: (state, data) => Object.assign(state, {
    form: Object.assign(state.form, {
      data,
    }),
  }),
  selectTemplate: (state, templateId) => Object.assign(state, {
    form: Object.assign(state.form, {
      templateId,
    }),
  }),
  selectRecipientsType: (state, newType) => {
    let recipientsType = null;

    if (newType === RECIPIENTS_TYPE_REGULAR) {
      recipientsType = RECIPIENTS_TYPE_REGULAR;
    } else if (
      newType === RECIPIENTS_TYPE_AUTOMATED
      || newType === RECIPIENTS_TYPE_MODERATED
    ) {
      recipientsType = RECIPIENTS_TYPE_AUTOMATED;
    }

    return Object.assign(state, {
      form: Object.assign(state.form, {
        recipientsType,
      }),
    });
  },
  setAttachedSegments: (state, attachedSegments) => Object.assign(state, {
    form: Object.assign(state.form, {
      attachedSegments: attachedSegments || [],
    }),
  }),
  setAttachedDocuments: (state, attachedDocuments) => Object.assign(state, {
    form: Object.assign(state.form, {
      attachedDocuments: attachedDocuments || [],
    }),
  }),
  setAudienceItems: (state, audienceItems) => Object.assign(state, {
    form: Object.assign(state.form, {
      audienceItems: audienceItems || [],
    }),
  }),
  setScheduleData: (state, scheduleData) => Object.assign(state, {
    form: Object.assign(state.form, {
      scheduleData,
    }),
  }),
  // template list mutations
  setTemplatesLoading: (state, isLoading) => Object.assign(state, {
    templates: Object.assign(state.templates, {
      isLoading,
    }),
  }),
  setTemplatesData: (state, data) => Object.assign(state, {
    templates: Object.assign(state.templates, {
      data,
    }),
  }),
  // single template mutations
  setSingleTemplateLoading: (state, isLoading) => Object.assign(state, {
    singleTemplate: Object.assign(state.singleTemplate, {
      isLoading,
    }),
  }),
  setSingleTemplateData: (state, data) => Object.assign(state, {
    singleTemplate: Object.assign(state.singleTemplate, {
      data,
    }),
  }),
  // company settings mutations
  setCompanySettingsLoading: (state, isLoading) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      isLoading,
    }),
  }),
  setCompanySettingsData: (state, data) => Object.assign(state, {
    companySettings: Object.assign(state.companySettings, {
      data,
    }),
  }),
  // system segment (Lists tab) mutations
  setSystemSegmentsLoading: (state, isLoading) => Object.assign(state, {
    systemSegments: Object.assign(state.systemSegments, {
      isLoading,
    }),
  }),
  setSystemSegmentsData: (state, data) => Object.assign(state, {
    systemSegments: Object.assign(state.systemSegments, {
      data: data || [],
    }),
  }),
};

const actions = {
  async updateCampaign(context, { id, payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      await newCampaignsApi('put', `/api/v1/campaigns/${id}`, {
        data: payload,
      });
      // re-populate campaign data
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async createCampaign(context, { payload, enableSaving = true }) {
    try {
      if (enableSaving) context.commit('setSaving', true);

      const res = await newCampaignsApi('post', '/api/v1/campaigns', {
        data: {
          ...payload,
          type: CHANNEL,
        },
      });

      const campaignId = res.data.id;

      await context.dispatch('getCampaign', {
        id: campaignId,
        enableLoading: false,
      });

      return campaignId;
    } finally {
      if (enableSaving) context.commit('setSaving', false);
    }
  },
  async forwardCampaign(context, payload) {
    try {
      context.commit('setSaving', true);

      const data = context.state.form.data;

      // create a brand new campaign cloned from the original's base fields
      const campaignId = await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: {
          name: data.name,
          subject: data.subject,
          content: data.content,
          is_recurrent: data.is_recurrent,
          // overrides from the setup step
          ...payload,
        },
      });

      // if the original campaign has a template, clone it and attach to the new campaign
      if (data.template_id) {
        const cloneRes = await newCampaignsApi('post', `/api/v1/templates/${data.template_id}/clone`, {
          data: {
            name: `${data.name} template`,
          },
        });

        await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/template`, {
          data: { template_id: cloneRes.data.id },
        });

        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
      }

      return campaignId;
    } finally {
      context.commit('setSaving', false);
    }
  },
  async createCampaignWithRecipient(context, { campaignPayload, recipientPayload }) {
    try {
      context.commit('setSaving', true);

      // create a static segment from recipient data
      const segmentRes = await newCampaignsApi('post', '/api/v1/segments', {
        data: {
          ...recipientPayload,
          type: 'static',
          channel: CHANNEL,
        },
      });

      const segmentId = segmentRes.data.id;

      const fullCampaignPayload = {
        ...campaignPayload,
        segment_ids: [segmentId],
      };

      // create campaign and return campaign id
      const campaignId = await context.dispatch('createCampaign', {
        enableSaving: false,
        payload: fullCampaignPayload,
      });

      // attach segment to campaign
      await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/segments`, {
        data: { segment_ids: [segmentId] },
      });

      return campaignId;
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCampaign(context, { id, enableLoading = true }) {
    try {
      if (enableLoading) context.commit('setLoading', true);

      const res = await newCampaignsApi('get', `/api/v1/campaigns/${id}`);
      const data = res.data;

      context.commit('selectRecipientsType', data.is_recurrent
        ? RECIPIENTS_TYPE_AUTOMATED
        : RECIPIENTS_TYPE_REGULAR);
      if (
        data.scheduled_at
        || data.recurrence_time
        || data.recurrence_timezone
        || !context.state.form.scheduleData
      ) {
        context.commit('setScheduleData', {
          scheduled_at: data.scheduled_at || null,
          recurrence_time: data.recurrence_time || null,
          recurrence_timezone: data.recurrence_timezone || null,
        });
      }
      // select template if available. Some campaign responses return the full
      // template object; others only include template_id.
      if (data.template && data.template.id) {
        context.commit('selectTemplate', data.template.id);
      } else if (data.template_id) {
        context.commit('selectTemplate', data.template_id);
      } else {
        context.commit('selectTemplate', null);
      }

      context.commit('setCampaignData', data);

      // refresh draft audience items (manual / file). Never let this break the
      // campaign load — a non-draft campaign or transient failure just yields [].
      try {
        await context.dispatch('getAudienceItems', { id });
      } catch (error) {
        context.commit('setAudienceItems', []);
      }

      try {
        await context.dispatch('getCampaignSegments', { id });
      } catch (error) {
        context.commit('setAttachedSegments', data.segments || []);
      }

      try {
        await context.dispatch('getCampaignAttachments', { id });
      } catch (error) {
        context.commit('setAttachedDocuments', []);
      }
    } catch (error) {
      if (campaignDoesNotBelongToCompany(error)) {
        throw new Error('UnauthorizedCompany');
      } else {
        throw error;
      }
    } finally {
      if (enableLoading) context.commit('setLoading', false);
    }
  },
  // ----- Audience (draft) -----
  async getAudienceItems(context, { id }) {
    const res = await newCampaignsApi('get', `/api/v1/campaigns/${id}/audience`);
    const items = (res.data && res.data.items) || [];
    context.commit('setAudienceItems', items);
    return items;
  },
  async getCampaignAttachments(context, { id } = {}) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);

    if (!campaignId) {
      context.commit('setAttachedDocuments', []);
      return [];
    }

    const res = await newCampaignsApi('get', `/api/v1/campaigns/${campaignId}/attachments`);
    const items = getCollectionItems(res.data);
    context.commit('setAttachedDocuments', items);

    return items;
  },
  async attachCampaignDocuments(context, { id, documentIds }) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);
    const ids = Array.isArray(documentIds) ? documentIds.filter((item) => item) : [];

    if (!campaignId) return [];

    if (ids.length > 0) {
      await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/attachments`, {
        data: {
          document_ids: ids,
        },
      });
    }

    return context.dispatch('getCampaignAttachments', { id: campaignId });
  },
  async detachCampaignDocument(context, { id, documentId }) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);

    if (!campaignId || !documentId) return [];

    await newCampaignsApi('delete', `/api/v1/campaigns/${campaignId}/attachments/${documentId}`);

    return context.dispatch('getCampaignAttachments', { id: campaignId });
  },
  async syncCampaignDocuments(context, { id, documentIds }) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);
    const selectedIds = Array.isArray(documentIds)
      ? [...new Set(documentIds.filter((item) => item).map((item) => String(item)))]
      : [];

    if (!campaignId) return [];

    const currentDocuments = await context.dispatch('getCampaignAttachments', { id: campaignId });
    const currentIds = currentDocuments
      .map((item) => item && (item.id || item.uid || item.document_uid || item.document_id))
      .filter((item) => item)
      .map((item) => String(item));

    const idsToAttach = selectedIds.filter((item) => !currentIds.includes(item));
    const idsToDetach = currentIds.filter((item) => !selectedIds.includes(item));

    if (idsToAttach.length > 0) {
      await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/attachments`, {
        data: {
          document_ids: idsToAttach,
        },
      });
    }

    await Promise.all(idsToDetach.map((documentId) => (
      newCampaignsApi('delete', `/api/v1/campaigns/${campaignId}/attachments/${documentId}`)
    )));

    return context.dispatch('getCampaignAttachments', { id: campaignId });
  },
  // stateless live count used while editing a filter — creates nothing
  async previewAudience(context, { id, filter, channel = null }) {
    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience/preview`, {
      data: { filter, channel },
    });
    return res.data;
  },
  async addFilterDraft(context, { label, filter }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'filter',
        label,
        channel: CHANNEL,
        filter,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async addSegmentAudience(context, { label, segmentId }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'segment',
        label,
        channel: CHANNEL,
        segment_id: segmentId,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  // search existing contacts by type + name (cursor-paginated)
  async searchContacts(context, {
    contactType, q, limit = 50, cursor,
  } = {}) {
    const res = await newCampaignsApi('get', '/api/v1/contacts', {
      params: {
        contact_type: contactType,
        channel: CHANNEL,
        limit,
        ...(q ? { q } : {}),
        ...(cursor ? { cursor } : {}),
      },
    });
    return res.data;
  },
  // add already-existing contacts as a manual draft item
  async addContactsAsManual(context, { label, contactIds }) {
    const id = context.state.form.data.id;

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'manual',
        label,
        channel: CHANNEL,
        contact_ids: contactIds,
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async addManualContact(context, payload) {
    const id = context.state.form.data.id;

    // create the contact first (tagged as a manual contact), then reference it
    // as a manual draft item
    const contactRes = await newCampaignsApi('post', '/api/v1/contacts', {
      data: { ...payload, contact_type: 'manual' },
    });
    const contact = contactRes.data;
    const label = contact.email || contact.name || 'Manual contact';

    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience`, {
      data: {
        kind: 'manual',
        label,
        channel: CHANNEL,
        contact_ids: [contact.id],
      },
    });

    await context.dispatch('getAudienceItems', { id });

    return res.data;
  },
  async uploadAudienceFile(context, { file, label }) {
    const id = context.state.form.data.id;

    const formData = new FormData();
    formData.append('file', file);

    // returns { item, imported: { ok, updated, error }, rows: [...] }
    const res = await newCampaignsApi('post', `/api/v1/campaigns/${id}/audience/file`, {
      params: {
        channel_type: FILE_CHANNEL_TYPE,
        label,
      },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    }, 'data');

    await context.dispatch('getAudienceItems', { id });

    return res;
  },
  async removeAudienceItem(context, { itemId }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('delete', `/api/v1/campaigns/${id}/audience/${itemId}`);
    await context.dispatch('getAudienceItems', { id });
  },
  // ----- Segments -----
  async getSegments(context, {
    contactType = 'shareholder',
    isSystem = true,
    channel = CHANNEL,
    limit = 50,
    q,
  } = {}) {
    const res = await newCampaignsApi('get', '/api/v1/segments', {
      params: {
        contact_type: contactType,
        is_system: isSystem,
        type: 'dynamic',
        channel,
        limit,
        ...(q ? { q } : {}),
      },
    });

    return (res.data && res.data.items) || (Array.isArray(res.data) ? res.data : []);
  },
  async getSystemSegments(context, { q } = {}) {
    try {
      context.commit('setSystemSegmentsLoading', true);

      const data = await context.dispatch('getSegments', {
        contactType: 'shareholder',
        isSystem: true,
        channel: CHANNEL,
        q,
      });
      context.commit('setSystemSegmentsData', data);
      return data;
    } finally {
      context.commit('setSystemSegmentsLoading', false);
    }
  },
  async getDefaultInvestorSegments() {
    const res = await newCampaignsApi('get', '/api/v1/segment-defaults', {
      params: {
        channel: CHANNEL,
      },
    });
    const segments = getCollectionItems(res.data);
    const defaultSegmentNames = ['new investors', 'investor exit', 'investors exit'];

    return segments.filter((segment) => {
      const name = String(segment.name || segment.label || '').trim().toLowerCase();
      return defaultSegmentNames.includes(name);
    });
  },
  async getCampaignSegments(context, { id } = {}) {
    const campaignId = id || (context.state.form.data && context.state.form.data.id);

    if (!campaignId) {
      context.commit('setAttachedSegments', []);
      return [];
    }

    const res = await newCampaignsApi('get', `/api/v1/campaigns/${campaignId}/segments`);
    const items = getCollectionItems(res.data);
    context.commit('setAttachedSegments', items);

    return items;
  },
  async attachSegments(context, { segmentIds }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('post', `/api/v1/campaigns/${id}/segments`, {
      data: { segment_ids: segmentIds },
    });
    await context.dispatch('getCampaignSegments', { id });
  },
  async detachSegment(context, { segmentId }) {
    const id = context.state.form.data.id;
    await newCampaignsApi('delete', `/api/v1/campaigns/${id}/segments/${segmentId}`);
    await context.dispatch('getCampaignSegments', { id });
  },
  async getSingleTemplate(context, { id, enableLoading = true, includeDesign = false }) {
    try {
      if (enableLoading) context.commit('setSingleTemplateLoading', true);

      const res = await newCampaignsApi('get', `/api/v1/templates/${id}`, {
        params: {
          ...(includeDesign ? { include_design: true } : {}),
        },
      });
      const data = res.data;

      context.commit('setSingleTemplateData', data);
      return data;
    } finally {
      if (enableLoading) context.commit('setSingleTemplateLoading', false);
    }
  },
  async applyTemplateToCampaign(context, { templateId }) {
    try {
      context.commit('setSaving', true);

      const campaignData = context.state.form.data;
      const campaignId = campaignData.id;
      const preservedCampaignFields = getCampaignSetupFields(
        campaignData,
        context.state.form.scheduleData,
      );
      const cloneRes = await newCampaignsApi('post', `/api/v1/templates/${templateId}/clone`, {
        data: {
          name: `${campaignData.name} template`,
          is_reusable: false,
        },
      });
      const campaignTemplateId = cloneRes.data.id;

      await newCampaignsApi('put', `/api/v1/templates/${campaignTemplateId}`, {
        data: {
          is_reusable: false,
        },
      });

      await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/template`, {
        data: { template_id: campaignTemplateId },
      });
      await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });

      const restoredPayload = getChangedTruthyFields(
        preservedCampaignFields,
        context.state.form.data,
      );

      if (Object.keys(restoredPayload).length > 0) {
        await newCampaignsApi('put', `/api/v1/campaigns/${campaignId}`, {
          data: restoredPayload,
        });
        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
      }

      context.commit('selectTemplate', campaignTemplateId);
    } finally {
      context.commit('setSaving', false);
    }
  },
  // Spam-warnings check is temporarily disabled — skip the request and report
  // no warnings so the template step continues without calling the endpoint.
  async testTemplate() {
    return [];
  },
  async getTemplates(context) {
    try {
      context.commit('setTemplatesLoading', true);

      const res = await newCampaignsApi('get', '/api/v1/templates', {
        params: {
          channel: CHANNEL,
        },
      });
      const data = res.data.items || res.data.data || (Array.isArray(res.data) ? res.data : []);

      context.commit('setTemplatesData', data);
    } catch (error) {
      console.error('[newEmailCampaign] getTemplates failed:', error);
    } finally {
      context.commit('setTemplatesLoading', false);
    }
  },
  // Persist the campaign editor state. The selected template is only the
  // starting point; editor saves also write the rendered HTML onto the campaign
  // so the campaign sends its own content.
  async saveTemplate(context, payload) {
    try {
      context.commit('setSaving', true);

      const campaignId = context.state.form.data.id;
      const selectedTemplateId = context.state.form.templateId;
      const preservedCampaignFields = getCampaignSetupFields(
        context.state.form.data,
        context.state.form.scheduleData,
      );
      const campaignTemplateId = context.state.form.data.template
        ? context.state.form.data.template.id
        : null;

      const isEditorSave = Object.prototype.hasOwnProperty.call(payload, 'body')
        || Object.prototype.hasOwnProperty.call(payload, 'design');
      const campaignTemplate = context.state.form.data.template || {};
      const templateIsReusable = campaignTemplate.is_reusable
        || (context.state.singleTemplate.data && context.state.singleTemplate.data.is_reusable);

      // If the campaign is still pointing at a reusable template, clone it
      // before editor writes so the library template is not mutated or saved
      // as a new reusable template by default.
      if (
        isEditorSave
        && String(selectedTemplateId) === String(campaignTemplateId)
        && templateIsReusable
      ) {
        const cloneRes = await newCampaignsApi('post', `/api/v1/templates/${campaignTemplateId}/clone`, {
          data: {
            name: `${context.state.form.data.name} template`,
            is_reusable: false,
          },
        });
        const newTemplateId = cloneRes.data.id;

        await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/template`, {
          data: { template_id: newTemplateId },
        });

        await newCampaignsApi('put', `/api/v1/templates/${newTemplateId}`, {
          data: {
            ...payload,
            is_reusable: false,
          },
        });

        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
        await context.dispatch('getSingleTemplate', {
          id: newTemplateId,
          enableLoading: false,
          includeDesign: true,
        });
      } else if (String(selectedTemplateId) === String(campaignTemplateId)) {
        await newCampaignsApi('put', `/api/v1/templates/${campaignTemplateId}`, {
          data: isEditorSave
            ? {
              ...payload,
              is_reusable: false,
            }
            : payload,
        });

        await context.dispatch('getSingleTemplate', {
          id: campaignTemplateId,
          enableLoading: false,
          includeDesign: Object.prototype.hasOwnProperty.call(payload, 'design'),
        });
      } else {
        // The user picked a reusable library template. Clone it so edits don't
        // mutate the shared template, attach the clone to the campaign, then
        // write the edited content onto the clone.
        const cloneRes = await newCampaignsApi('post', `/api/v1/templates/${selectedTemplateId}/clone`, {
          data: {
            name: payload.name || `${context.state.form.data.name} template`,
            is_reusable: false,
          },
        });
        const newTemplateId = cloneRes.data.id;

        await newCampaignsApi('post', `/api/v1/campaigns/${campaignId}/template`, {
          data: { template_id: newTemplateId },
        });

        await newCampaignsApi('put', `/api/v1/templates/${newTemplateId}`, {
          data: isEditorSave
            ? {
              ...payload,
              is_reusable: false,
            }
            : payload,
        });

        // refresh the campaign (so data.template + form.templateId track the
        // attached clone) and the single template data (so the editor reloads it)
        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
        await context.dispatch('getSingleTemplate', {
          id: newTemplateId,
          enableLoading: false,
          includeDesign: Object.prototype.hasOwnProperty.call(payload, 'design'),
        });
      }

      if (isEditorSave && Object.prototype.hasOwnProperty.call(payload, 'body')) {
        await newCampaignsApi('put', `/api/v1/campaigns/${campaignId}`, {
          data: {
            content: payload.body,
          },
        });
        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
      }

      const restoredPayload = getChangedTruthyFields(
        preservedCampaignFields,
        context.state.form.data,
      );

      if (Object.keys(restoredPayload).length > 0) {
        await newCampaignsApi('put', `/api/v1/campaigns/${campaignId}`, {
          data: restoredPayload,
        });
        context.commit('setScheduleData', {
          scheduled_at: restoredPayload.scheduled_at
            || preservedCampaignFields.scheduled_at
            || null,
          recurrence_time: restoredPayload.recurrence_time
            || preservedCampaignFields.recurrence_time
            || null,
          recurrence_timezone: restoredPayload.recurrence_timezone
            || preservedCampaignFields.recurrence_timezone
            || null,
        });
        await context.dispatch('getCampaign', { id: campaignId, enableLoading: false });
      }
    } finally {
      context.commit('setSaving', false);
    }
  },
  async getCompanySettings(context) {
    const { commit, rootGetters } = context;
    const companyId = rootGetters['company/company_id'];

    commit('setCompanySettingsLoading', true);

    try {
      const response = await newCampaignsApi('get', '/api/v1/companies', {
        params: { ids: companyId },
      });

      const list = response.data.items || response.data;
      const company = Array.isArray(list) ? list[0] : list;

      commit('setCompanySettingsData', company || null);
    } catch (error) {
      console.error('Failed to fetch company settings:', error);
    } finally {
      commit('setCompanySettingsLoading', false);
    }
  },
  async updateCompanySettings(context, payload) {
    const companyId = context.rootGetters['company/company_id'];
    await newCampaignsApi('put', `/api/v1/companies/${companyId}`, {
      data: payload,
    });
    // refresh company data
    await context.dispatch('getCompanySettings');
  },
  async scheduleCampaign(context, { id, payload }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/schedule`, {
        data: payload,
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async sendCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/send`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async pauseCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/pause`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async resumeCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/resume`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
  async stopCampaign(context, { id }) {
    try {
      context.commit('setSaving', true);

      await newCampaignsApi('post', `/api/v1/campaigns/${id}/stop`, {
        data: {},
      });
      await context.dispatch('getCampaign', { id, enableLoading: false });
    } finally {
      context.commit('setSaving', false);
    }
  },
};

const getSegmentId = (segment) => (
  segment.segment_id
  || segment.id
  || segment.uid
  || (segment.segment && segment.segment.id)
);

const getSegmentName = (segment) => (
  segment.name
  || segment.label
  || (segment.segment && (segment.segment.name || segment.segment.label))
  || 'Unnamed list'
);

const getSegmentCount = (segment) => (
  segment.member_count
  || segment.contact_count
  || segment.count
  || (segment.segment && (
    segment.segment.member_count
    || segment.segment.contact_count
    || segment.segment.count
  ))
  || 0
);

// normalize an attached segment into the unified recipient-group shape
const normalizeSegment = (segment) => ({
  uid: getSegmentId(segment),
  itemType: 'segment',
  source: 'list',
  name: getSegmentName(segment),
  count: getSegmentCount(segment),
  count_unique: getSegmentCount(segment),
  status: segment.status || null,
  segment_id: getSegmentId(segment),
});

// normalize a draft audience item into the unified recipient-group shape
const normalizeAudienceItem = (item) => {
  const count = item.count != null ? item.count : (item.row_count || 0);
  const source = item.kind || item.type || 'manual';
  const normalizedSource = source === 'segment' ? 'list' : source;

  return {
    uid: item.id,
    itemType: 'draft',
    source: normalizedSource,
    name: item.label,
    count,
    count_unique: count,
    status: item.status || 'pending',
    segment_id: item.segment_id || null,
  };
};

const getters = {
  newTemplateLayouts: (state) => state.templates.data,
  premadeLayouts: () => [],
  myLayouts: () => [],
  // unified list combining attached segments + draft audience items
  recipientGroups: (state) => [
    ...state.form.attachedSegments.map(normalizeSegment),
    ...state.form.audienceItems.map(normalizeAudienceItem),
  ],
  listRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.itemType === 'segment' || item.source === 'list'),
  fileRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'file'),
  filterRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'filter'),
  manualRecipients: (state, sgetters) => sgetters.recipientGroups
    .filter((item) => item.source === 'manual'),
  totalRecipientCount: (state, sgetters) => sgetters.recipientGroups
    .reduce((acc, item) => acc + (item.count || 0), 0),
  totalEmailCount: (state, sgetters) => sgetters.recipientGroups
    .reduce((acc, item) => acc + (item.count_unique != null ? item.count_unique : item.count), 0),
  // send/schedule preflight passes with >=1 attached segment OR >=1 draft item
  hasRecipients: (state, sgetters) => sgetters.recipientGroups.length > 0,
  // audience controls (add/file/delete) are allowed while the campaign is a
  // draft or still scheduled (not yet processing/sent)
  canEditAudience: (state) => !!state.form.data
    && (state.form.data.status === 'draft' || state.form.data.status === 'scheduled'),
  // "Save as draft" only applies to a brand-new (not yet persisted) campaign or
  // one that is still a draft. Once it's scheduled/sent it's no longer a draft,
  // so the action is hidden.
  canSaveDraft: (state) => !state.form.data || state.form.data.status === 'draft',
  campaignsFeatureIsSetup: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.email_enabled,
  campaignsFeatureIsActive: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.client_status
    && state.companySettings.data.client_status === 'active',
  hasSmartSearchEnabled: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.smart_search_enabled,
  hasShareholderSendingEnabled: (state) => state.companySettings
    && state.companySettings.data
    && state.companySettings.data.shareholder_sending_enabled,
  // ids of system segments already attached to the campaign (for the Lists tab)
  attachedSegmentIdMap: (state) => state.form.attachedSegments.reduce((acc, item) => {
    const id = getSegmentId(item);
    if (id) acc[id] = true;
    return acc;
  }, state.form.audienceItems.reduce((acc, item) => {
    const source = item.kind || item.type;
    if ((source === 'segment' || source === 'list') && item.segment_id) {
      acc[item.segment_id] = true;
    }
    return acc;
  }, {})),
};

export default {
  state: InitialState,
  mutations,
  actions,
  getters,
  namespaced: true,
};
